class TestimonialSlider {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error('Testimonial slider container not found:', containerId);
      return;
    }

    this.sliderContainer = this.container.closest('.testimonial-slider-container');
    this.prevButton = this.sliderContainer.querySelector('.slider-button--prev');
    this.nextButton = this.sliderContainer.querySelector('.slider-button--next');
    this.paginationDots = this.sliderContainer.querySelectorAll('.pagination-dot');
    this.slides = this.container.querySelectorAll('.testimonial-slide');
    this.currentSlideIndex = 0;

    // These values are approximate and depend on CSS flex-basis and gap
    this.desktopItemsToShow = 3; // Assuming 3 items visible on desktop
    this.tabletItemsToShow = 2;  // Assuming 2 items visible on tablet
    this.mobileItemsToShow = 1;  // Assuming 1 item visible on mobile

    this.init();
  }

  init() {
    if (this.slides.length === 0) {
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
      if (this.paginationDots.length > 0) {
        this.paginationDots[0].closest('.slider-pagination').style.display = 'none';
      }
      return;
    }

    this.prevButton.addEventListener('click', this.goToPrevSlide.bind(this));
    this.nextButton.addEventListener('click', this.goToNextSlide.bind(this));
    this.container.addEventListener('scroll', this.debounce(this.updateSliderUI.bind(this), 50)); // Debounce scroll event
    window.addEventListener('resize', this.debounce(this.handleResize.bind(this), 100));

    this.paginationDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Initial update
    this.updateSliderUI();
    this.handleResize(); // Set correct itemsToShow based on initial size
  }

  debounce(func, delay) {
    let timeout;
    return function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  }

  getSlidesPerPage() {
    const width = window.innerWidth;
    if (width <= 768) return this.mobileItemsToShow;
    if (width <= 1024) return this.tabletItemsToShow;
    return this.desktopItemsToShow;
  }

  getScrollPositionForSlide(index) {
    if (this.slides.length === 0) return 0;

    const slide = this.slides[index];
    const slideOffsetLeft = slide.offsetLeft;
    
    // Get computed gap from the track (will be applied between elements)
    const style = getComputedStyle(this.container);
    const gap = parseFloat(style.gap || '0');
    
    // For single slide view (mobile), align to start of slide
    if (this.getSlidesPerPage() === 1) {
      return slideOffsetLeft;
    }

    // For multiple slides per view, calculate scroll to bring the 'first' slide of the group into view
    // This is more complex. A simpler approach for multiple-item display is to scroll by the width of one item + gap.
    // Or, for consistent pagination, scroll to the start of the 'page' of slides.
    // Given the request for simple left/right, scrolling by one slide is usually sufficient.
    const scrollAmount = (slide.offsetWidth + gap) * index;
    return scrollAmount;
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.currentSlideIndex = index;
    const scrollPosition = this.getScrollPositionForSlide(this.currentSlideIndex);
    this.container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    this.updateSliderUI();
  }

  goToNextSlide() {
    const slidesPerPage = this.getSlidesPerPage();
    let nextIndex = this.currentSlideIndex + 1;

    const isAtEnd = (this.container.scrollLeft + this.container.clientWidth) >= (this.container.scrollWidth - 1); 
    if (isAtEnd) {
        return; // Already at the end
    }

    // Determine the furthest index we can scroll to while still showing 'slidesPerPage' items
    const maxScrollableIndex = this.slides.length - slidesPerPage;
    if (nextIndex > maxScrollableIndex) {
        nextIndex = maxScrollableIndex; // Scroll to the point where the last full set starts
    }
    
    // Ensure nextIndex doesn't go out of bounds
    if (nextIndex < 0) nextIndex = 0; // Should not happen with next
    if (nextIndex >= this.slides.length) nextIndex = this.slides.length - 1; // Ensure it's a valid index

    this.currentSlideIndex = nextIndex;
    this.goToSlide(this.currentSlideIndex);
  }

  goToPrevSlide() {
    let prevIndex = this.currentSlideIndex - 1;
    if (prevIndex < 0) {
      prevIndex = 0;
    }
    this.currentSlideIndex = prevIndex;
    this.goToSlide(this.currentSlideIndex);
  }

  updateSliderUI() {
    if (this.slides.length === 0) return;

    const scrollLeft = this.container.scrollLeft;
    const scrollWidth = this.container.scrollWidth;
    const clientWidth = this.container.clientWidth;

    // Calculate current slide based on scroll position
    const slideWidth = this.slides[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(this.container).gap || '0');
    if (slideWidth + gap > 0) {
      // Find the currently most visible slide
      this.currentSlideIndex = Math.round(scrollLeft / (slideWidth + gap));
    } else {
      this.currentSlideIndex = 0; // Fallback
    }

    // Update arrow button states
    this.prevButton.disabled = scrollLeft <= 0;
    this.nextButton.disabled = Math.ceil(scrollLeft) + clientWidth >= scrollWidth - 1;

    // Hide/show arrows if there are not enough slides to scroll or on mobile/tablet
    const slidesVisible = this.getSlidesPerPage();
    if (this.slides.length <= slidesVisible || window.innerWidth <= 1024) { // <= 1024px for tablet/mobile
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
    } else {
      this.prevButton.style.display = ''; // Revert to CSS default (block)
      this.nextButton.style.display = '';
    }

    // Update pagination dots (hide if fewer slides than visible per page)
    const paginationWrapper = this.paginationDots[0]?.closest('.slider-pagination');
    if (this.slides.length <= slidesVisible && paginationWrapper) {
        paginationWrapper.style.display = 'none';
    } else if (paginationWrapper) {
        paginationWrapper.style.display = ''; // Show pagination
    }

    this.paginationDots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentSlideIndex);
      dot.tabIndex = index === this.currentSlideIndex ? 0 : -1;
    });
  }

  handleResize() {
    this.updateSliderUI();
  }
}